import * as Sentry from '@sentry/react';
import { logger } from 'utils/logger';
import { store } from 'store/store';
import { selectCurrentSystemRole, selectIsSystemSession } from 'features/application/selectors';
import { logsAttributes } from 'utils/sentry/telemetryEvent';
import {
  WasmProbe,
  WebGLClassification,
  WebGLProbe,
  WebGpuClassification,
} from './capabilityProbe';
import { VBGateFailedCondition } from './gateErrors';

export { logsAttributes } from 'utils/sentry/telemetryEvent';

const VB_LOG = { system: true, capture: 'virtual-background' } as const;

export type GateVerdict = 'ok' | 'no-webgl' | 'software' | 'no-wasm' | 'error';

// 'error' (our probe threw) proceeds to the SDK's own read: the gate refuses only on
// positive evidence — software renderer, absent WASM, or a persistently-null WebGL read.
export const gateVerdict = (wasm: WasmProbe, webgl: WebGLProbe): GateVerdict => {
  if (webgl.classification === 'error') {
    return 'error';
  }
  if (!wasm.present) {
    return 'no-wasm';
  }
  if (webgl.classification === 'software') {
    return 'software';
  }
  if (!webgl.webgl2 && !webgl.webgl1) {
    return 'no-webgl';
  }
  return 'ok';
};

export interface GateProbeState {
  ok: boolean;
  wasm: WasmProbe;
  initialWebGL: WebGLProbe;
  finalWebGL: WebGLProbe;
  webgpu: WebGpuClassification;
  msSincePageLoad: number;
  reprobeCount: number;
  recoveredAfterMs: number | null;
  windowExhaustedEarlier: boolean;
}

export interface GateRecord {
  outcome: 'refused' | 'passed';
  failedCondition: VBGateFailedCondition | null;
  classification: WebGLClassification;
  webgl2: boolean;
  webgl1: boolean;
  renderer: string | null;
  vendor: string | null;
  wasmPresent: boolean;
  wasmSimd: boolean;
  webgpu: WebGpuClassification;
  msSincePageLoad: number;
  initialVerdict: GateVerdict;
  finalVerdict: GateVerdict;
  reprobeCount: number;
  recoveredAfterMs: number | null;
  windowExhaustedEarlier: boolean;
  attempt: number;
  sdkDisagreement: boolean;
  role: string;
  isSystemSession: boolean;
  release: string | undefined;
}

const failedConditionOf = (verdict: GateVerdict): VBGateFailedCondition => {
  switch (verdict) {
    case 'no-wasm':
      return 'wasm';
    case 'software':
      return 'software';
    case 'no-webgl':
      return 'webgl';
    default:
      // Our probe passed (or could not judge) and the SDK still refused.
      return 'unknown';
  }
};

let attemptCounter = 0;

export const buildGateRecord = (
  state: GateProbeState,
  sdkSupported: boolean | null
): GateRecord => {
  const initialVerdict = gateVerdict(state.wasm, state.initialWebGL);
  const finalVerdict = gateVerdict(state.wasm, state.finalWebGL);
  const outcome = state.ok && sdkSupported === true ? 'passed' : 'refused';
  const reduxState = store.getState();

  attemptCounter += 1;

  return {
    outcome,
    failedCondition: outcome === 'passed' ? null : failedConditionOf(finalVerdict),
    classification: state.finalWebGL.classification,
    webgl2: state.finalWebGL.webgl2,
    webgl1: state.finalWebGL.webgl1,
    renderer: state.finalWebGL.renderer,
    vendor: state.finalWebGL.vendor,
    wasmPresent: state.wasm.present,
    wasmSimd: state.wasm.simd,
    webgpu: state.webgpu,
    msSincePageLoad: state.msSincePageLoad,
    initialVerdict,
    finalVerdict,
    reprobeCount: state.reprobeCount,
    recoveredAfterMs: state.recoveredAfterMs,
    windowExhaustedEarlier: state.windowExhaustedEarlier,
    attempt: attemptCounter,
    sdkDisagreement: sdkSupported === false && finalVerdict === 'ok',
    role: selectCurrentSystemRole(reduxState) ?? 'human',
    isSystemSession: selectIsSystemSession(reduxState),
    release: process.env.REACT_APP_RELEASE_VERSION,
  };
};

// Closed enum, never the raw count: the Sentry tag-values endpoint truncates to the
// top 9 values, and reprobeCount alone has 11.
const vbReprobeTag = (record: GateRecord): 'none' | 'recovered' | 'exhausted' => {
  if (record.recoveredAfterMs !== null) {
    return 'recovered';
  }
  if (record.reprobeCount > 0 || record.windowExhaustedEarlier) {
    return 'exhausted';
  }
  return 'none';
};

const GATE_REFUSED_MESSAGE = '[VB] gate refused';

const GATE_REFUSAL_REASON: Record<VBGateFailedCondition, string> = {
  wasm: 'no WASM',
  webgl: 'no WebGL',
  software: 'software renderer',
  unknown: 'unknown reason',
};

const gateRefusalReason = (record: GateRecord): string => {
  if (record.sdkDisagreement) {
    return 'SDK disagreement';
  }
  // Our probe threw, so the gate proceeded on no evidence and the SDK refused unopposed.
  if (record.finalVerdict === 'error') {
    return 'probe errored, SDK unsupported';
  }
  return GATE_REFUSAL_REASON[record.failedCondition ?? 'unknown'];
};

export const emitGateRecord = (
  record: GateRecord,
  { captureIssue }: { captureIssue: boolean }
): void => {
  const refused = record.outcome === 'refused';
  const message = refused
    ? `${GATE_REFUSED_MESSAGE} — ${gateRefusalReason(record)}`
    : '[VB] gate passed';

  logger.remote(VB_LOG)[refused ? 'warn' : 'info'](message, record);

  // Safe no-op when Sentry.init never ran (sentryEnabled=false rooms).
  Sentry.logger[refused ? 'warn' : 'info'](
    message,
    logsAttributes({ ...record, event: 'vb.gate' })
  );

  if (refused && captureIssue) {
    // Title stays reason-free so the issue keeps one stable name across all refusal causes.
    Sentry.captureMessage(GATE_REFUSED_MESSAGE, {
      level: 'warning',
      fingerprint: ['vb-gate-refused'],
      tags: {
        vbGate: record.outcome,
        vbFailedCondition: record.failedCondition ?? 'unknown',
        vbClassification: record.classification,
        vbReprobe: vbReprobeTag(record),
      },
      extra: { ...record },
    });
  }
};

export type VBIntentSource = 'panel' | 'sdk-init' | 'sdk-command';

export type VBIntentSignal =
  | { source: 'panel' }
  | { source: 'sdk-init' | 'sdk-command'; roomJoined: boolean; enforce: boolean };

// Integrator-loop guard: an automated caller re-issuing the SDK command is caught by
// count and timing, not by content — count and cap it here.
const MAX_SDK_INTENT_SIGNALS = 10;

let panelIntentLogged = false;
let sdkIntentCount = 0;

const INTENT_SOURCE_LABEL: Record<VBIntentSource, string> = {
  panel: 'settings panel',
  'sdk-init': 'SDK init',
  'sdk-command': 'SDK command',
};

const emitIntent = (
  source: VBIntentSource,
  payload: Record<string, string | number | boolean>
): void => {
  const message = `[VB] intent — ${INTENT_SOURCE_LABEL[source]}`;
  const record = { source, ...payload };

  logger.remote(VB_LOG).info(message, record);
  Sentry.logger.info(message, logsAttributes({ ...record, event: 'vb.intent' }));
};

export const logVBIntent = (signal: VBIntentSignal): void => {
  const msSincePageLoad = performance.now();

  if (signal.source === 'panel') {
    if (panelIntentLogged) {
      return;
    }
    panelIntentLogged = true;
    emitIntent(signal.source, { msSincePageLoad });
    return;
  }

  if (sdkIntentCount >= MAX_SDK_INTENT_SIGNALS) {
    return;
  }
  sdkIntentCount += 1;

  emitIntent(signal.source, {
    roomJoined: signal.roomJoined,
    enforce: signal.enforce,
    msSincePageLoad,
    commandIndex: sdkIntentCount,
  });
};
